/**
 * Calculates year revenue and gross margin totals from historical data.
 *
 * IMPORTANT: If a yearly (YTD) data point exists, it REPLACES all monthly/quarterly values.
 * This is because yearly data points represent Year-To-Date snapshots that already include
 * all prior activity for the year.
 *
 * Behavior:
 * - If yearly data point exists: Use ONLY that value (it's the YTD total)
 * - If no yearly data point: Sum quarterly + monthly values (cumulative)
 *
 * @param {Array} historicalData - Array of data points with date, revenue, grossMargin, and type
 * @param {number} year - Optional year to calculate totals for (defaults to current year)
 * @returns {Object} { totalRevenue, totalGrossMargin }
 */
export const calculateYearTotals = (historicalData, year = null) => {
  if (!historicalData || historicalData.length === 0) {
    console.log('📊 calculateYearTotals: No data provided');
    return { totalRevenue: 0, totalGrossMargin: 0 };
  }

  const targetYear = year || new Date().getFullYear();
  console.log('📊 calculateYearTotals: Target year is', targetYear);
  console.log('📊 calculateYearTotals: All data points:', historicalData.map(p => ({
    date: p.date,
    year: new Date(p.date).getFullYear(),
    type: p.type,
    revenue: p.revenue,
    grossMargin: p.grossMargin
  })));

  const currentYearData = historicalData.filter(point =>
    new Date(point.date).getFullYear() === targetYear
  );

  console.log('📊 calculateYearTotals: Current year data count:', currentYearData.length);

  if (currentYearData.length === 0) {
    console.log('📊 calculateYearTotals: No data for current year');
    return { totalRevenue: 0, totalGrossMargin: 0 };
  }

  // Group by type
  const yearlyData = currentYearData.filter(point => point.type === 'yearly');
  const quarterlyData = currentYearData.filter(point => point.type === 'quarterly');
  const monthlyData = currentYearData.filter(point => point.type === 'monthly');

  let totalRevenue = 0;
  let totalGrossMargin = 0;

  // If there's a yearly data point, use ONLY that (it's a YTD snapshot that REPLACES cumulative values)
  // This fixes the bug where YTD values were being ADDED to monthly/quarterly instead of replacing them
  if (yearlyData.length > 0) {
    const latestYearly = yearlyData.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    totalRevenue = latestYearly.revenue || 0;
    totalGrossMargin = latestYearly.grossMargin || 0;
    console.log('📊 calculateYearTotals: Using yearly YTD snapshot (replaces monthly/quarterly):', { totalRevenue, totalGrossMargin });
  } else {
    // No yearly data point - sum quarterly and monthly values (cumulative)
    if (quarterlyData.length > 0) {
      totalRevenue += quarterlyData.reduce((sum, point) => sum + (point.revenue || 0), 0);
      totalGrossMargin += quarterlyData.reduce((sum, point) => sum + (point.grossMargin || 0), 0);
    }

    if (monthlyData.length > 0) {
      totalRevenue += monthlyData.reduce((sum, point) => sum + (point.revenue || 0), 0);
      totalGrossMargin += monthlyData.reduce((sum, point) => sum + (point.grossMargin || 0), 0);
    }
    console.log('📊 calculateYearTotals: Summed monthly/quarterly values:', { totalRevenue, totalGrossMargin });
  }

  return { totalRevenue, totalGrossMargin };
};
